/* eslint-disable prettier/prettier */
import { TreatyEntityType } from '../../../types/TreatyEntityType';
import { emojiIconMap } from '../../../utils/emojiIconMap';
import { getFieldValue } from '../../../utils/getFieldValue';
import { KeyValuePair } from '../../ui/KeyValuePair';
import { CollapsibleSection } from '../CollapsibleSection';
import { useCurrencyOptions } from '../../../hooks/useCurrencyOptions';
import {
  parseISO,
  isValid,
  format,
  addYears,
  differenceInCalendarMonths,
  differenceInCalendarYears,
  differenceInDays,
  parse,
} from 'date-fns';

export const GeneralInfoSection = ({
  treaty,
  refreshTreatyData,
}: {
  treaty: any;
  refreshTreatyData?: () => void;
}) => {
  const fields = [
    'programNumber',
    'UMR',
    'mainLineOfBusiness',
    'subLineOfBusiness',
    'typeOfTreaty',
    'businessTitle',
    'inceptionDate',
    'expiryDate',
    'underwritingYear',
    'multiYear',
    'contractDuration',
    'mainTerritorialScope',
    'contractCurrency',
    'claimBasis',
    'accountingBasis',
    'brokerageRate',
    'taxesRate',
  ];

  const { currencies } = useCurrencyOptions();

  const fieldConfigMap: Record<string, any> = {
    typeOfTreaty: {
      type: 'select',
      options: ['Proportional', 'Non-Proportional'],
    },
    contractCurrency: {
      type: 'multi-select',
      options: currencies.map((code) => ({
        label: code.value,
        value: code.value,
      })),
    },
    claimBasis: {
      type: 'select',
      options: ['Loss Occurrence', 'Risk Attaching'],
      linkedField: 'accountingBasis',
      linkLogic: (val: string) =>
        val === 'Loss Occurrence' ? 'Accident Year' : 'Underwriting Year',
    },
    accountingBasis: {
      type: 'select',
      options: ['Accident Year', 'Underwriting Year'],
      linkedField: 'claimBasis',
      linkLogic: (val: string) => (val === 'Accident Year' ? 'Loss Occurrence' : 'Risk Attaching'),
    },
    brokerageRate: {
      type: 'percent',
    },
    taxesRate: {
      type: 'percent',
    },
    inceptionDate: {
      type: 'date',
      linkedField: 'expiryDate',
      linkLogic: (val: string) => {
        if (!val) return '';
        const parsedDate = parse(val, 'MM/dd/yyyy', new Date());
        if (!isValid(parsedDate)) return '';
        return format(addYears(parsedDate, 1), 'MM/dd/yyyy');
      },
    },
    expiryDate: {
      type: 'date-or-continuous',
    },
    underwritingYear: {
      type: 'text',
      editable: false,
    },

    multiYear: {
      type: 'text',
      editable: false,
    },

    contractDuration: {
      type: 'text',
      editable: false,
    },
  };

  const inceptionRaw = getFieldValue(treaty['inceptionDate']).value;
  const expiryValue = getFieldValue(treaty['expiryDate']).value;

  const inception = parse(inceptionRaw, 'MM/dd/yyyy', new Date());
  const expiry = expiryValue !== 'Continuous' ? parse(expiryValue, 'MM/dd/yyyy', new Date()) : null;

  const underwritingYear = isValid(inception) ? inception.getFullYear().toString() : '';

  const multiYear =
    expiryValue === 'Continuous' ||
    (expiry && isValid(expiry) && differenceInCalendarMonths(expiry, inception) > 12)
      ? 'Yes'
      : 'No';

  const contractDuration =
    expiryValue === 'Continuous'
      ? 'Continuous'
      : isValid(expiry)
        ? `${differenceInCalendarYears(expiry!, inception)}y ${differenceInCalendarMonths(expiry!, inception) % 12}m ${differenceInDays(expiry!, inception) % 30}d`
        : '';

  return (
    <CollapsibleSection title="General Info" icon={emojiIconMap['General Info']}>
      {(() => {
        const overrideValues: Record<string, string> = {
          underwritingYear,
          multiYear,
          contractDuration,
        };

        return fields.map((fieldKey) => {
          const { value, ai_result } = getFieldValue(treaty[fieldKey]);

          const extraData =
            fieldKey === 'expiryDate'
              ? { linkedInceptionDate: getFieldValue(treaty['inceptionDate']).value }
              : {};

          return (
            <KeyValuePair
              key={fieldKey}
              label={fieldKey.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
              value={overrideValues[fieldKey] ?? value}
              aiValue={ai_result}
              entityId={treaty.id}
              entityType={TreatyEntityType.TREATY}
              fieldKey={fieldKey}
              refreshTreatyData={refreshTreatyData}
              fieldConfig={{
                ...fieldConfigMap[fieldKey],
              }}
              editable={fieldConfigMap[fieldKey]?.editable ?? true}
              extraData={extraData}
            />
          );
        });
      })()}
    </CollapsibleSection>
  );
};
